'use client'

import { useEffect } from 'react'
import { usePathname, useRouter } from 'next/navigation'
import { getAuthToken, decodeToken } from '@/lib/auth'
import { setInterviewEntryFlow } from '@/lib/interviewEntryFlow'

/**
 * Global guard that locks enterprise candidates to /interview (JD video shell).
 *
 * Reads identity_type from the signed JWT so clearing or manipulating
 * sessionStorage cannot bypass the restriction.
 *
 * The only legitimate non-/interview route for an enterprise candidate is
 * /dashboard — and only during the initial email→token-validation entry, which
 * sets the one-time 'jd_dashboard_open_embedded' key via armJdDashboardOpen()
 * before navigating there. Once that key is consumed the candidate is locked to
 * /interview for the rest of the session.
 *
 * Legacy /jd-interview URLs redirect to /interview via app/jd-interview/page.tsx.
 */
export default function EnterpriseInterviewLockGuard() {
  const pathname = usePathname()
  const router = useRouter()

  useEffect(() => {
    if (typeof window === 'undefined') return

    const token = getAuthToken()
    if (!token) return

    const payload = decodeToken(token) as { identity_type?: string } | null
    if (payload?.identity_type !== 'enterprise_candidate') return

    setInterviewEntryFlow('jd')

    if (pathname === '/interview' || pathname.startsWith('/interview/')) return
    if (pathname === '/jd-interview' || pathname.startsWith('/jd-interview/')) return
    if (pathname === '/enterprise/interview-consent') return
    if (pathname === '/') return

    if (pathname === '/dashboard' || pathname.startsWith('/dashboard/')) {
      try {
        if (sessionStorage.getItem('jd_dashboard_open_embedded') === '1') return
      } catch {
        /* storage disabled */
      }
    }

    router.replace('/interview')
  }, [pathname, router])

  return null
}
